"use client";

import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { VerifyMfaCodeApi, ResendMfaCodeApi } from "@/services/AuthServices";
import { toast } from "sonner";

const mfaSchema = z.object({
  code: z
    .string()
    .regex(/^\d{6}$/, { message: "Enter the 6-digit code" })
    .min(6, { message: "Code must be 6 digits" }),
});

interface MfaChallengeProps {
  ticket: string;
  email?: string;
  onSuccess: (user: any) => void;
  onCancel: () => void;
}

export const MfaChallenge = ({ ticket, email, onSuccess, onCancel }: MfaChallengeProps) => {
  const form = useForm<z.infer<typeof mfaSchema>>({
    resolver: zodResolver(mfaSchema),
    defaultValues: { code: "" },
  });

  const [isResending, setIsResending] = useState(false);

  const handleVerify = async (values: z.infer<typeof mfaSchema>) => {
    try {
      const response = await VerifyMfaCodeApi(ticket, values.code);
      onSuccess(response.data.user);
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || "Verification failed";
      toast.error(message);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      await ResendMfaCodeApi(ticket);
      toast.success("Verification code sent");
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || "Unable to resend code";
      toast.error(message);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleVerify)}
        className="w-full max-w-md bg-white dark:bg-zinc-800 rounded-3xl shadow-xl px-8 py-10 flex flex-col gap-6 text-gray-800 dark:text-gray-100"
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <h2 className="text-2xl font-bold">Verify your identity</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            A 6-digit code was just sent to <span className="font-semibold">{email || "your email"}</span>. Enter it
            below to finish signing in.
          </p>
        </div>

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Verification Code
              </FormLabel>
              <FormControl>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  autoComplete="one-time-code"
                  {...field}
                  className="p-4 text-center tracking-widest text-lg rounded-lg shadow-sm border border-gray-300 dark:border-gray-600 focus:border-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none uppercase"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 cursor-pointer text-white rounded-lg py-3 px-6 font-semibold text-lg shadow-md transition duration-300 ease-in-out hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          disabled={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? "Verifying..." : "Verify"}
        </Button>

        <div className="flex flex-col gap-3 text-sm text-center text-gray-600 dark:text-gray-300">
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending}
            className="text-blue-600 dark:text-blue-400 font-semibold hover:underline disabled:opacity-60"
          >
            {isResending ? "Sending..." : "Didn't get a code? Resend"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            Use a different account
          </button>
        </div>
      </form>
    </Form>
  );
};
